import { getSimpleInstance } from '@ctn/axios-util';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { useErrorMessageStore } from '@/store/error-message.store';
import { useSessionStore } from '@/store/session.store';

import { ROUTES } from '@/constants/routes.constants';

const BASE_URL_PUBLIC = process.env.REACT_APP_API_URL_PUBLIC ?? '';
const BASE_URL_SECURE = process.env.REACT_APP_API_URL_SECURE ?? '';
const BASE_URL_BDCORPO = `${process.env.REACT_APP_API_URL_SECURE ?? ''}/bdcorpo/maestros`;
const BASE_URL_BDCORPO_NOTARIA = `${process.env.REACT_APP_API_URL_SECURE ?? ''}/bdcorpo/`;
const tokenKey = process.env.REACT_APP_JWT_PUBKEY ?? '';
const extraProps = { app: process.env.REACT_APP_JWT_APP ?? '' };
const tokenEndpoint = process.env.REACT_APP_JWT_URL ?? '';

const forceLogout = () => {
	window.location.href = ROUTES.PORTADA;
	sessionStorage.clear();
};

/**
 * Interceptor para las peticiones al backend, en caso de tener
 * informada una credencial en el sessionStorage esta se adjunta
 * en la cabecera para ser enviada en cada petición.
 */
const interceptorLoadHeaders = async (config: InternalAxiosRequestConfig) => {
	const { user } = useSessionStore.getState();
	const { token, username } = user ?? {};
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
		config.headers.Usuario = username;
	}
	return config;
};

type Error = {
	code: string;
	data: undefined;
	errorMessage: string;
	errorType: string;
	status: number;
	success: boolean;
};

/**
 * Interceptor de errores de Axios para la gestión global de respuestas HTTP.
 * Este manejador centraliza la lógica de seguridad basándose en los códigos de estado 401 y 403:
 *
 * Error 401 (No autorizado):
 * - Si el mensaje es 'TOKEN_NO_ENCONTRADO', se asume que la sesión expiró o es inexistente.
 * - Se ejecuta un cierre de sesión forzado y se notifica al usuario.
 * - En cualquier otro caso de 401 (ej. falta de permisos específicos), se muestra el error
 *   proporcionado por el servidor y se redirige al usuario a la home privada (HOME).
 *
 * Error 403 (Prohibido):
 * - Se interpreta como un intento de manipulación o acceso a servicios restringidos (trapeo).
 * - Por seguridad, se cierra la sesión inmediatamente y se muestra el mensaje de error.
 */
const interceptorError = async (error: AxiosError<Error>): Promise<never> => {
	const { setErrorMessage } = useErrorMessageStore.getState();
	const responseData = error?.response?.data;

	if (error?.response?.status === 401) {
		if (responseData?.errorMessage === 'TOKEN_NO_ENCONTRADO' || responseData?.errorMessage === 'TOKEN_CADUCADO') {
			forceLogout();
			setErrorMessage('Le informamos que la sesión se ha cerrado.');
		} else {
			const msg = responseData?.errorMessage ?? 'Acceso no autorizado.';
			setErrorMessage(msg);
			window.location.href = ROUTES.HOME;
		}
	}

	if (error?.response?.status === 403) {
		forceLogout();
		setErrorMessage('Acceso no autorizado.');
	}

	return Promise.reject(error);
};

const axiosInstance = axios.create({ baseURL: BASE_URL_SECURE });

const normalizeError = (error: any) => {
	const normalizedError = error instanceof Error ? error : new Error(error);
	return Promise.reject(normalizedError);
};

axiosInstance.interceptors.request.use(interceptorLoadHeaders, (error) => normalizeError(error));
axiosInstance.interceptors.response.use((response) => response, interceptorError);

const axiosInstanceBdCorpo = axios.create({ baseURL: BASE_URL_BDCORPO });
axiosInstanceBdCorpo.interceptors.request.use(interceptorLoadHeaders, (error) => normalizeError(error));
axiosInstanceBdCorpo.interceptors.response.use((response) => response, interceptorError);

const axiosInstanceBdCorpoNotaria = axios.create({ baseURL: BASE_URL_BDCORPO_NOTARIA });
axiosInstanceBdCorpoNotaria.interceptors.request.use(interceptorLoadHeaders, (error) => normalizeError(error));
axiosInstanceBdCorpoNotaria.interceptors.response.use((response) => response, interceptorError);


const noTokenInstance = getSimpleInstance({ baseURL: BASE_URL_PUBLIC });

export { axiosInstanceBdCorpo, noTokenInstance, axiosInstanceBdCorpoNotaria };

export default axiosInstance;
